import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Package } from 'lucide-react';
import { alertsApi } from '../services/alertsApi';
import { SparePart } from '../types';

interface SparePartsModalProps {
  isOpen: boolean;
  onClose: () => void;
  faultCategory: string | null;
  equipmentName?: string;
  siteCode?: string;
  onSpareSelect?: (spareDescription: string) => void;
}

export const SparePartsModal: React.FC<SparePartsModalProps> = ({
  isOpen,
  onClose,
  faultCategory,
  equipmentName,
  siteCode,
  onSpareSelect
}) => {
  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpareId, setSelectedSpareId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<string>('maintenance');
  const [consumeQuantity, setConsumeQuantity] = useState<number>(1);
  const [consuming, setConsuming] = useState(false);

  useEffect(() => {
    if (isOpen && faultCategory) {
      fetchSpareParts();
    } else {
      setSpareParts([]);
      setError(null);
      setSelectedSpareId(null);
      setConsumeQuantity(1);
      setActionType('maintenance');
    }
  }, [isOpen, faultCategory, siteCode]);

  const fetchSpareParts = async () => {
    if (!faultCategory) return;

    setLoading(true);
    setError(null);

    try {
      const data = await alertsApi.fetchSpareParts(faultCategory, equipmentName, siteCode);
      setSpareParts(data);
    } catch (err: any) {
      console.error('Error fetching spare parts:', err);
      setError(err?.message || 'Failed to load spare parts data');
      setSpareParts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (partId: number) => {
    setSelectedSpareId(prevId => {
      if (prevId === partId) return null;
      setConsumeQuantity(1); // Reset quantity when selecting new item
      return partId;
    });
  };

  const handleSaveSelection = () => {
    if (selectedSpareId !== null && onSpareSelect) {
      const selectedPart = spareParts.find(part => part.id === selectedSpareId);
      if (selectedPart) {
        // Construct description string
        const spareDescription = selectedPart.description ||
          (selectedPart.product_serial_number ? `Serial: ${selectedPart.product_serial_number}` : null) ||
          `Spare ID: ${selectedSpareId}`;

        const fullDescription = `${spareDescription} (Qty: ${consumeQuantity})`;

        // Pass data back to parent - The parent (AlertRow) ensures the type of the callback matches what it sets in editCache
        // We pass arguments individually or as an object depending on what the parent expects.
        // Actually, onSpareSelect is defined in props as (desc: string) => void.
        // We need to cast it or change the prop definition to pass extra data.
        // Since we can't easily change the prop type in all usages without checking, let's use a "hack" or just assume the parent knows.
        // Wait, I can pass a custom object if I change the interface.
        // Let's rely on the parent updating the editCache with extra fields.

        // I will change the prop signature in the interface below.
        (onSpareSelect as any)(fullDescription, selectedSpareId, consumeQuantity, actionType);

        onClose();
      }
    }
  };

  const columns = useMemo(() => [
    { key: 'select', label: 'Select' },
    { key: 'equipment_name', label: 'Equipment Name' },
    { key: 'category', label: 'Selected Category' },
    { key: 'description', label: 'Description' },
    { key: 'product_serial_number', label: 'Product Serial No.' },
    { key: 'datasheet', label: 'Datasheet' },
    { key: 'electrical_rating', label: 'Electrical Rating' },
    { key: 'make', label: 'OEM' },
    { key: 'installed_quantity', label: 'Installed Qty' },
    { key: 'spare_availability', label: 'Availability' },
    { key: 'consumption_pattern_last_three_months', label: 'Consump (3M)' },
    { key: 'consumption_pattern_last_six_months', label: 'Consump (6M)' },
    { key: 'consumption_pattern_last_twelve_months', label: 'Consump (1Y)' },
    { key: 'unit_price', label: 'Latest Price' },
    { key: 'supplier', label: 'Latest Vendor' },
    { key: 'lead_time', label: 'Lead Time' },
    { key: 'min_stock', label: 'Min Qty' },
    { key: 'max_stock', label: 'Max Qty' },
    { key: 'notes', label: 'Alternate' },
  ], []);

  const renderCellValue = (part: SparePart, key: string) => {
    const value = part[key as keyof SparePart];
    if (value === null || value === undefined) return '-';
    return String(value);
  };

  const getSelectedPartAvailability = () => {
    const part = spareParts.find(p => p.id === selectedSpareId);
    return part ? Number(part.spare_availability || 0) : 0;
  };

  if (!isOpen) return null;

  return (
    <div className="spare-parts-modal-overlay" onClick={onClose}>
      <div className="spare-parts-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="spare-parts-modal-header">
          <div className="spare-parts-modal-title">
            <Package size={20} />
            <div>
              <h3>Spare Management</h3>
              <p className="spare-parts-modal-subtitle">
                Category: <strong>{faultCategory || 'N/A'}</strong>
                {equipmentName && <span> | Equipment: <strong>{equipmentName}</strong></span>}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {spareParts.length > 0 && selectedSpareId !== null && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 500 }}>Action:</label>
                  <select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px'
                    }}
                  >
                    <option value="maintenance">Maintenance</option>
                    <option value="replacement">Replacement</option>
                    <option value="new_install">New Install</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 500 }}>Qty:</label>
                  <input
                    type="number"
                    min="1"
                    max={getSelectedPartAvailability()}
                    value={consumeQuantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const max = getSelectedPartAvailability();
                      if (val >= 1 && val <= max) setConsumeQuantity(val);
                    }}
                    style={{
                      width: '60px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                  />
                </div>
              </>
            )}
            {spareParts.length > 0 && (
              <button
                className="btn btn-success"
                onClick={handleSaveSelection}
                disabled={selectedSpareId === null || consuming}
                style={{
                  padding: '6px 12px',
                  backgroundColor: selectedSpareId === null ? '#ccc' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedSpareId === null ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {/* consuming state is no longer used for API calls, but we check !consuming relative to old logic, keep it clean */}
                Select Spare Part
              </button>
            )}
            <button className="spare-parts-modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="spare-parts-modal-body">
          {loading ? (
            <div className="spare-parts-loading">
              <Loader2 className="spinner" size={32} />
              <p>Loading spare parts data...</p>
            </div>
          ) : error ? (
            <div className="spare-parts-error">
              <p>{error}</p>
            </div>
          ) : spareParts.length === 0 ? (
            <div className="spare-parts-empty">
              <Package size={48} />
              <p>No spare parts found for category: "{faultCategory}"</p>
            </div>
          ) : (
            <div className="spare-parts-table-container">
              <table className="spare-parts-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {spareParts.map((part) => (
                    <tr key={part.id}>
                      {columns.map((col) => {
                        if (col.key === 'select') {
                          return (
                            <td key={col.key} style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedSpareId === part.id}
                                onChange={() => handleCheckboxChange(part.id)}
                                disabled={consuming || Number(part.spare_availability || 0) <= 0}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            </td>
                          );
                        }

                        if (col.key === 'spare_availability') {
                          const avail = Number(part.spare_availability || 0);
                          return (
                            <td key={col.key}>
                              <span style={{
                                fontWeight: 'bold',
                                color: avail > 0 ? '#10b981' : '#ef4444'
                              }}>
                                {avail}
                              </span>
                            </td>
                          );
                        }

                        if (col.key === 'datasheet' && part.datasheet) {
                          return (
                            <td key={col.key}>
                              <a href={part.datasheet} target="_blank" rel="noopener noreferrer" style={{ color: '#0084CF', textDecoration: 'underline' }}>
                                View
                              </a>
                            </td>
                          );
                        }

                        return (
                          <td key={col.key}>
                            {renderCellValue(part, col.key)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

